import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SimpleGroup } from "src/api/generated/frontend_internal_v1";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "src/components/base/dropdown";
import { EllipsisVerticalIcon, PencilSquareIcon } from "@heroicons/react/20/solid";
import UpdateGroupDialog from "src/components/dialogs/update-group";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import WS from "src/api/ws";
import TablePagination from "src/components/table-pagination";
import { TenantBadge } from "src/components/badges/tenant";

/**
 * The properties for {@link ScimGroupOverview}
 */
export type ScimGroupOverviewProps = {};

const LIMIT = 25;

/**
 * An overview of all available groups
 */
function ScimGroupOverview(props: ScimGroupOverviewProps) {
    const [t] = useTranslation("admin-groups");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { search, page } = Route.useSearch();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.GroupsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    const [openUpdateGroup, setOpenUpdateGroup] = React.useState<SimpleGroup>();

    return (
        <div className={"flex flex-col gap-6"}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/a/groups/scim",
                                        search: {
                                            page: 1,
                                            search: fieldApi.state.value,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>

            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.tenant")}</TableHeader>
                        <TableHeader>{t("label.group-name")}</TableHeader>
                        <TableHeader>{t("label.internet-access")}</TableHeader>
                        <TableHeader>{t("label.description")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((group) => (
                        <TableRow key={group.uuid} href={"/a/groups/$groupId/general"} params={{ groupId: group.uuid }}>
                            <TableCell>{group.tenant && <TenantBadge tenant={group.tenant} />}</TableCell>
                            <TableCell>{group.name}</TableCell>
                            <TableCell>
                                {group.internet ? <CheckCircleIcon color={"green"} className={"size-6"} /> : undefined}
                            </TableCell>
                            <TableCell className={"w-[50ch] text-wrap break-all whitespace-pre-wrap"}>
                                {group.description}
                            </TableCell>
                            <TableCell>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownItem onClick={() => setOpenUpdateGroup(group)}>
                                            <PencilSquareIcon />
                                            <DropdownLabel>{t("button.update-group")}</DropdownLabel>
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/groups/scim"}
                getSearchParams={(newPage: number) => ({ search, page: newPage })}
            />

            <Suspense>
                <UpdateGroupDialog
                    isOpen={!!openUpdateGroup}
                    onClose={() => setOpenUpdateGroup(undefined)}
                    onUpdate={() => {
                        setOpenUpdateGroup(undefined);
                        navigate({ to: "/a/groups/scim", search: { search: "", page } });
                    }}
                    group={openUpdateGroup}
                />
            </Suspense>
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type GroupsOverviewSearch = {
    /** The search term that should be used */
    search: string;
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_admin/a/groups/_groups/scim")({
    component: ScimGroupOverview,
    validateSearch: (search: Record<string, unknown>): GroupsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page, search: (search.search as string) || "" };
    },
    loaderDeps: ({ search: { search, page } }) => ({ search, page }),
    loader: async ({ deps }) =>
        await ApiInternal.groups.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
            provider: "Scim",
        }),
});
